/**
 * Main hook for SmswiserCampaignPreparedDropsTab.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '@/providers/ToastProvider';
import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';
import { getApiErrorMessage } from '@/lib/utils';
import {
  useBulkUpdatePreparedDropsFlex,
  useCountryCreative,
  useCreatePreparedDrop,
  useCreatePreparedDropFlex,
  useBulkCreatePreparedDrops,
  useDeleteCountrySender,
  useDeleteCountrySendersBulk,
  useDeleteCountryText,
  useDeleteCountryTextsBulk,
  useDeletePreparedDropFlex,
  useBulkDeletePreparedDropsFlex,
  usePreparedDropsByCountry,
  useReorderPreparedDropsFlex,
  useSendPreparedDropFlex,
  useUpdateCountryCreative,
  useUpdatePreparedDropFlex,
} from '@/hooks';
import {
  useCountry,
  useGatewayPerformanceTags,
  useLeadsListsByCountry,
  useListPerfTagsByCampaign,
  useListPerformanceTags,
  useTextPerformanceTags,
} from '@/hooks';

import { getDefaultCampaign } from '@/features/smswiser-campaigns/utils/defaultCampaignStorage';
import { buildListTagQueryParams } from '@/features/smswiser-campaigns/utils/listTagQueryParams';

import { useBulkSaveAndSend } from './useBulkSaveAndSend';
import { useCreativeHandlers } from './useCreativeHandlers';
import { usePreparedDropsActions } from './usePreparedDropsActions';

export interface CreatePreparedFlexMutation {
  mutateAsync: (params: { wiserId: number; payload: object }) => Promise<unknown>;
}

export interface UseSmswiserCampaignPreparedDropsTabParams {
  countryKey: string;
  campaigns: Array<{ id: number; name: string }>;
  wiserId: number;
  workDate: string;
  campaignName: string;
  titlePrefix: string;
  defaultSubTab?: 'drafts' | 'sent';
  onSelectCampaign: (id: number) => void;
  highlightPreparedDropId?: string | null;
  onInvestigationFocusConsumed?: () => void;
}

export function useSmswiserCampaignPreparedDropsTab(props: UseSmswiserCampaignPreparedDropsTabParams) {
  const {
    countryKey,
    campaigns,
    wiserId,
    workDate,
    titlePrefix,
    campaignName,
    defaultSubTab = 'drafts',
    onSelectCampaign,
    highlightPreparedDropId,
    onInvestigationFocusConsumed,
  } = props;

  const [showPrepare, setShowPrepare] = useState(false);
  const [showPlusOne, setShowPlusOne] = useState(false);
  const [formCampaignId, setFormCampaignId] = useState(0);
  const handleSentSuccess = useCallback(() => setShowPlusOne(true), []);

  useEffect(() => {
    if (showPrepare) {
      const defaultId = getDefaultCampaign(countryKey);
      const valid = defaultId != null && campaigns.some((c) => c.id === defaultId);
      setFormCampaignId(valid ? defaultId : 0);
    }
  }, [showPrepare, countryKey, campaigns]);

  const handleFormCampaignChange = useCallback(
    (id: number) => {
      setFormCampaignId(id);
      onSelectCampaign(id);
    },
    [onSelectCampaign],
  );

  useEffect(() => {
    if (!showPlusOne) return;
    const t = setTimeout(() => setShowPlusOne(false), 1800);
    return () => clearTimeout(t);
  }, [showPlusOne]);

  useEffect(() => setOptimisticDrops(null), [workDate]);

  const activeCampaignId = formCampaignId || wiserId;
  const perfWiserIds = activeCampaignId ? [activeCampaignId] : undefined;

  const country = useCountry(countryKey, workDate);
  const leadsListsQuery = useLeadsListsByCountry(countryKey);
  /** Same params as the stats tab so both surfaces share cache records. */
  const listTagParams = useMemo(() => buildListTagQueryParams(), []);
  const perfTagsQuery = useListPerformanceTags(countryKey, perfWiserIds, listTagParams);
  const textPerfTagsQuery = useTextPerformanceTags(countryKey, perfWiserIds);
  const gatewayPerfTagsQuery = useGatewayPerformanceTags(countryKey, perfWiserIds);
  const prepared = usePreparedDropsByCountry(countryKey, workDate);
  const creative = useCountryCreative(countryKey);

  const createPrepared = useCreatePreparedDrop(countryKey, wiserId);
  const createPreparedFlex = useCreatePreparedDropFlex();
  const bulkCreatePrepared = useBulkCreatePreparedDrops();
  const updatePreparedFlex = useUpdatePreparedDropFlex();
  const bulkUpdatePreparedFlex = useBulkUpdatePreparedDropsFlex();
  const reorderPreparedFlex = useReorderPreparedDropsFlex();
  const deletePreparedFlex = useDeletePreparedDropFlex();
  const bulkDeletePreparedFlex = useBulkDeletePreparedDropsFlex();
  const sendPreparedFlex = useSendPreparedDropFlex();
  const updateCreative = useUpdateCountryCreative(countryKey);
  const deleteCampaignSender = useDeleteCountrySender(countryKey);
  const deleteCampaignSendersBulk = useDeleteCountrySendersBulk(countryKey);
  const deleteCampaignText = useDeleteCountryText(countryKey);
  const deleteCampaignTextsBulk = useDeleteCountryTextsBulk(countryKey);
  const { success: toastSuccess, error: toastError } = useToast();
  const [optimisticDrops, setOptimisticDrops] = useState<PreparedDrop[] | null>(null);

  const baseDrops = prepared.data ?? [];
  const drops = optimisticDrops ?? baseDrops;

  /** Tag scope for the drops list: one query per DISTINCT campaign among the
   *  loaded drops (a handful), so each row resolves tags in ITS campaign's
   *  scope and every query reuses the stats tab's per-campaign cache. */
  const dropCampaignIds = useMemo(() => {
    const ids = new Set<number>();
    for (const d of drops) if (Number.isFinite(d.wiserId)) ids.add(d.wiserId);
    return [...ids].sort((a, b) => a - b);
  }, [drops]);
  const dropTagsByCampaign = useListPerfTagsByCampaign(countryKey, dropCampaignIds);
  const dropListTags = useMemo(
    () => ({
      tagsForDrop: (d: PreparedDrop) =>
        dropTagsByCampaign.byCampaign[d.wiserId]?.tagsByWiserId?.[d.leadsListWiserId],
      tagColors: dropTagsByCampaign.tagColors,
      isLoading: dropTagsByCampaign.isLoading,
    }),
    [dropTagsByCampaign],
  );

  const handleReorder = useCallback(
    (updates: Array<{ id: string; orderIndex: number }>) =>
      new Promise<void>((resolve, reject) => {
        const current = optimisticDrops ?? baseDrops;
        const orderMap = new Map(updates.map((u) => [u.id, u.orderIndex]));
        const reordered: PreparedDrop[] = [...current]
          .map((d) => ({ ...d, orderIndex: orderMap.get(d.id) ?? d.orderIndex ?? 0 }))
          .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
        setOptimisticDrops(reordered);
        reorderPreparedFlex.mutate(
          { countryKey, workDate, updates },
          {
            onSuccess: () => {
              setOptimisticDrops(null);
              toastSuccess('Order updated', 'Drafts reordered.');
              resolve();
            },
            onError: (err) => {
              setOptimisticDrops(null);
              toastError('Reorder failed', getApiErrorMessage(err, 'Could not reorder drafts'));
              reject(err);
            },
          },
        );
      }),
    [countryKey, workDate, baseDrops, optimisticDrops, reorderPreparedFlex, toastSuccess, toastError],
  );
  const actions = usePreparedDropsActions({
    countryKey,
    drops,
    prepared,
    onSentSuccess: handleSentSuccess,
    createPreparedFlex: createPreparedFlex as unknown as {
      mutateAsync: (params: {
        countryKey: string;
        wiserId: number;
        payload: Record<string, unknown>;
      }) => Promise<unknown>;
    },
    bulkCreatePrepared: bulkCreatePrepared as unknown as {
      mutateAsync: (params: {
        countryKey: string;
        wiserId: number;
        items: Record<string, unknown>[];
      }) => Promise<unknown>;
    },
  });

  const handleBulkSendComplete = useCallback(
    (result: { sent: number; failed: number; errorMessages?: string[] }) => {
      prepared.refetch();
      if (result.sent > 0) handleSentSuccess();
    },
    [prepared, handleSentSuccess],
  );

  const creativeHandlers = useCreativeHandlers({
    creative,
    updateCreative,
    deleteCampaignSender,
    deleteCampaignSendersBulk,
    deleteCampaignText,
    deleteCampaignTextsBulk,
  });

  const handleBulkSaveAndSend = useBulkSaveAndSend({
    drops,
    prepared,
    bulkUpdatePreparedFlex,
    handleBulkSend: actions.handleBulkSend,
    handleBulkSendComplete,
  });

  const routes = country.data?.routes ?? [];
  const rawLeadsLists =
    (workDate ? country.data?.leadsLists : null) ??
    leadsListsQuery.data ??
    country.data?.leadsLists ??
    [];
  const leadsLists = useMemo(() => {
    const byId = perfTagsQuery.data?.tagsByWiserId ?? {};
    return rawLeadsLists.map((l) => ({
      ...l,
      performanceTags: byId[l.id] ?? [],
    }));
  }, [rawLeadsLists, perfTagsQuery.data?.tagsByWiserId]);

  /** Tag filter options: only tags on lists (+ New / Unused). */
  const availableTags = useMemo(() => {
    const set = new Set<string>(['New', 'Unused']);
    const byWiserId = perfTagsQuery.data?.tagsByWiserId ?? {};
    for (const tags of Object.values(byWiserId)) {
      for (const t of tags) {
        const s = String(t).trim();
        if (s) set.add(s);
      }
    }
    return [...set].sort();
  }, [perfTagsQuery.data?.tagsByWiserId]);

  const tlds = country.data?.shortLinkTlds ?? [];

  const isBusy = Boolean(
    country.isLoading ||
      leadsListsQuery.isLoading ||
      prepared.isLoading ||
      createPrepared.isPending ||
      createPreparedFlex.isPending ||
      bulkCreatePrepared.isPending ||
      updatePreparedFlex.isPending ||
      bulkUpdatePreparedFlex.isPending ||
      deletePreparedFlex.isPending ||
      bulkDeletePreparedFlex.isPending ||
      sendPreparedFlex.isPending ||
      creative.isLoading ||
      updateCreative.isPending ||
      deleteCampaignSender.isPending ||
      deleteCampaignSendersBulk.isPending ||
      deleteCampaignText.isPending ||
      actions.killDrop?.isPending ||
      actions.editDrop?.isPending ||
      actions.editDropBatch?.isPending,
  );

  return {
    ...props,
    countryKey,
    updateCreative,
    deleteCampaignSender,
    deleteCampaignText,
    deleteCampaignTextsBulk,
    campaigns,
    wiserId,
    workDate,
    campaignName,
    titlePrefix,
    defaultSubTab,
    onSelectCampaign,
    highlightPreparedDropId,
    onInvestigationFocusConsumed,
    showPrepare,
    setShowPrepare,
    showPlusOne,
    country,
    leadsListsQuery,
    perfTagsQuery,
    dropListTags,
    textPerfTagsQuery,
    gatewayPerfTagsQuery,
    prepared,
    creative,
    createPrepared,
    createPreparedFlex,
    updatePreparedFlex,
    bulkUpdatePreparedFlex,
    reorderPreparedFlex,
    deletePreparedFlex,
    bulkDeletePreparedFlex,
    sendPreparedFlex,
    routes,
    leadsLists,
    availableTags,
    tagsByTextId: textPerfTagsQuery.data?.tagsByTextId,
    tagsByGatewayId: gatewayPerfTagsQuery.data?.tagsByGatewayId,
    tlds,
    drops,
    isBusy,
    actions,
    handleBulkSendComplete,
    ...creativeHandlers,
    handleBulkSaveAndSend,
    handleSentSuccess,
    handleReorder,
    formCampaignId,
    handleFormCampaignChange,
  };
}
